import React, { useCallback, useMemo } from 'react'
import { Row, Button, useContainerWidth, IconButton } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import { DocumentRecordPerson, ContentAttach, DocumentStory, NoticeInfo, NoticeQuestion } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { hasTranscript, useSlsStateContext, getImage } from '@fs/zion-sls'
import { getIndexExploreContextUrl } from '../../utils/utils'
import { useErrorStatus } from '../../../Lightyear/views/imageBrowse/hooks'
import { useOverlayContext } from '../../providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext } from '../../../RIP/providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../providers/sharedBrowserProvider/SharedBrowserContext'

// The zion help overlay package is deprecated with no replacement, so the help button links to
// the Help Center in a new tab instead of opening an in-page overlay.
// This is the Help Center landing page rather than a search: a `?query=` term would stay in
// English for every locale, sending non-English users to a localized Help Center to search for
// an English phrase it does not index.
// No locale segment here on purpose: zion's Link prepends the active one, and a hardcoded
// locale would be treated as already-correct and never updated.
const HELP_CENTER_URL = 'https://www.familysearch.org/help/helpcenter'

export default function NavButtons({ saveRecordOverlay, showNavButtons }) {
  const { t } = useTranslation()
  const atContainerWidth = useContainerWidth()
  const { query, setQuery } = useQueryParams({ replaceState: true })
  const { errorStatus } = useErrorStatus()
  const { infoSheetOverlay, closeAll, allPanelsClosed } = useOverlayContext()
  const r2Dispatch = useR2DispatchContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const contextUrl = getIndexExploreContextUrl()
  const zionSlsState = useSlsStateContext()

  const currentImage = useMemo(
    () => getImage(zionSlsState.images, zionSlsState.currentImageArk),
    [zionSlsState.images, zionSlsState.currentImageArk]
  )

  // Determine if the header is hidden (same logic as in RootBase.js)
  const isHeaderHidden = Boolean(contextUrl)
  // Always show help button when header is hidden
  const showHelpButton = isHeaderHidden

  const showTranscriptButton = useMemo(() => {
    return hasTranscript(currentImage?.elements)
  }, [currentImage])

  // Determine if the help button should be visible based on screen size and header visibility
  const isHelpButtonVisible = useMemo(() => {
    // Always show the help button when it's available, regardless of screen size
    // This ensures it's visible when the header is hidden
    return showHelpButton
  }, [showHelpButton])

  const isWideScreen = atContainerWidth({
    default: false, // Icon-only for smallest screens
    md: false, // Still icon-only > 600px
    lg: false, // Still icon-only > 750px
    xl: false, // Still icon-only > 950px
    xxl: true, // Text buttons only at xxl > 1200px
  })

  const imageError = sharedBrowserState.imageError
  const isImageRestricted = Boolean(imageError) && imageError.status !== 'OK' && imageError.status !== 'UNAVAILABLE'

  // A view button does nothing when its panel is already showing, or when the image can't be viewed
  // (the info sheet won't open), so disable it and explain why in its tooltip.
  // While a child panel is open the button still closes it, so it isn't current then
  const getViewButtonProps = (view, openDescription) => {
    const isCurrent = query.view === view && Boolean(infoSheetOverlay?.isOpen) && allPanelsClosed()
    return {
      disabled: isImageRestricted || isCurrent,
      disabledDescription: isImageRestricted
        ? t('headerNavigation.unavailableForImage', "Not available because this image can't be viewed.")
        : openDescription,
      'aria-current': isCurrent ? 'true' : undefined,
    }
  }
  const namesButtonProps = getViewButtonProps(
    'index',
    t('headerNavigation.namesOpen', 'The Names panel is already open.')
  )
  const transcriptButtonProps = getViewButtonProps(
    'fullText',
    t('headerNavigation.transcriptOpen', 'The Transcript panel is already open.')
  )
  const informationButtonProps = getViewButtonProps(
    'explore',
    t('headerNavigation.informationOpen', 'The Information panel is already open.')
  )

  const handleViewChange = useCallback(
    (newView) => {
      setQuery({
        view: newView,
        personArk: undefined, // Reset personArk param when changing views
        ...(query.from && { from: query.from }),
      })

      // Close any open panels when changing views
      closeAll()

      // Remove the personArk from the state if the view is explore.
      if (newView === 'explore') {
        r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', value: null })
      }

      if (!isImageRestricted) {
        // Open the info sheet when changing views via nav buttons
        infoSheetOverlay.open()
      }
    },
    [setQuery, query.from, isImageRestricted, r2Dispatch, infoSheetOverlay, closeAll]
  )

  // Identical in both the wide and narrow layouts, so build it once
  const helpButton = isHelpButtonVisible && (
    <IconButton
      title={t('headerNavigation.help', 'Help')}
      Icon={NoticeQuestion}
      to={HELP_CENTER_URL}
      target="_blank"
      rel="noopener noreferrer"
      external
      data-testid="help-button"
      linkName="header-nav-help"
      color="gray"
    />
  )

  // Both apps render these; each parent hides them while a mode or panel takes over the header
  // (Lightyear: organize/metadata/audit modes; RIP: while Add Names is open)
  if (!showNavButtons) return null

  if (isWideScreen) {
    return (
      <Row gutters="xs" alignY="middle">
        <Button
          noShrinkX
          color="gray"
          Icon={DocumentRecordPerson}
          onClick={() => handleViewChange('index')}
          data-testid="names-button"
          {...namesButtonProps}
          linkName="header-nav-names"
        >
          {t('headerNavigation.names', 'Names')}
        </Button>

        {showTranscriptButton && (
          <Button
            noShrinkX
            color="gray"
            Icon={DocumentStory}
            onClick={() => handleViewChange('fullText')}
            data-testid="transcript-button"
            {...transcriptButtonProps}
            linkName="header-nav-transcript"
          >
            {t('headerNavigation.transcript', 'Transcript')}
          </Button>
        )}

        <Button
          noShrinkX
          color="gray"
          Icon={NoticeInfo}
          onClick={() => handleViewChange('explore')}
          data-testid="information-button"
          {...informationButtonProps}
          linkName="header-nav-information"
        >
          {t('headerNavigation.information', 'Information')}
        </Button>

        <Button
          noShrinkX
          emphasis="high"
          data-testid="save-record-button"
          disabled={!!errorStatus}
          linkName="save-record"
          {...saveRecordOverlay.menuButtonProps}
        >
          {t('headerNavigation.saveRecord', 'Save Record')}
        </Button>

        {helpButton}
      </Row>
    )
  }

  // For all other screen sizes, use icon buttons to save space
  return (
    <Row alignY="middle" gutters="xs">
      <IconButton
        title={t('headerNavigation.names', 'Names')}
        Icon={DocumentRecordPerson}
        onClick={() => handleViewChange('index')}
        data-testid="names-button"
        {...namesButtonProps}
        linkName="header-nav-names"
        color="gray"
      />

      {showTranscriptButton && (
        <IconButton
          title={t('headerNavigation.transcript', 'Transcript')}
          Icon={DocumentStory}
          onClick={() => handleViewChange('fullText')}
          data-testid="transcript-button"
          {...transcriptButtonProps}
          linkName="header-nav-transcript"
          color="gray"
        />
      )}

      <IconButton
        title={t('headerNavigation.information', 'Information')}
        Icon={NoticeInfo}
        onClick={() => handleViewChange('explore')}
        data-testid="information-button"
        {...informationButtonProps}
        linkName="header-nav-information"
        color="gray"
      />

      <IconButton
        noShrinkX
        title={t('headerNavigation.saveRecord', 'Save Record')}
        Icon={ContentAttach}
        data-testid="save-record-button"
        disabled={!!errorStatus}
        linkName="save-record"
        {...saveRecordOverlay.menuButtonProps}
      />

      {helpButton}
    </Row>
  )
}
